import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NavBar from "../../../Admin/Dashboard/NavBar";
import ReactProSidebar from "../../../Admin/Dashboard/ReactProSidebar";
import "./../../../../styles/TopicQuery.css";
import "./../../../../styles/TopicQueryCardPlatfroms.css";
import PlatformsSelect from "./components/TopicSelect";
import { modelsApi } from "../../../../API/axiosInstance";
import EditModal from "./components/EditModal";
import CustomDrawer from "./components/TopicViewDrawer";
import { useLocation, useNavigate } from "react-router-dom";
import { MyContext } from "../../../../MyContextApi";
import TopicFilters from "./components/TopicFilters";
import { useCountdownTimers } from "./hooks/useCountdownTimers";
import useTopicsSSE from "./hooks/useTopicsSSE";
import TopicCards from "./components/TopicCards";


const TopicQuery = () => {
  const [isRefetching, setIsRefetching] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allTopics, setAllTopics] = useState([]);
  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  // Add filters state
  const [filters, setFilters] = useState({
    platform: "",
    sortBy: "newest",
    pageSize: 10,
    type: "",
  });
  const { formData, setFormData } = useContext(MyContext);
  const [modalOpen, setModalOpen] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewdrawerOpen, setViewDrawerOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedTopicIndex, setSelectedTopicIndex] = useState(null);
  const token = localStorage.getItem("Token");
  const location = useLocation();
  const navigate = useNavigate();
  const selectedTopic =
    selectedTopicIndex !== null ? allTopics[selectedTopicIndex] : null;
  const { connStatus: sseStatus, openSSE, closeSSE } = useTopicsSSE({ setAllTopics });

  const [globalCount, setGlobalCount] = useState({
    totalCount: 0,
    thisWeekCount: 0,
    from: null,
    to: null,
  });
  const [userCount, setUserCount] = useState({
    totalCount: 0,
    thisWeekCount: 0,
    email: "",
    username: "",
    from: null,
    to: null,
  });

  const handleEdit = async (id) => {
    try {
      setLoading(true);

      const response = await modelsApi.get(`/topics`, {
        params: { topic_id: id }, // ✅ query param
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      //
      setSessionData(data);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching topic data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (updatedData) => {
    // Refetch data after update
    getAllTopics();
  };

  const handleInputChange = (field, value) =>
    setFormData({ ...formData, [field]: value });

  const handleConfirm = () => {
    handleNewTopic();
    //
    setDrawerOpen(false);
  };

  const handleNewTopic = () => {
    const payload = {
      topic: formData.topic,
      type: formData.type,
      platform: formData.platforms,
    };
    modelsApi
      .post(`/topics`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          getAllTopics();
          setFormData({
            topic: "",
            description: "",
            toggle: false,
            type: "",
            platforms: [],
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching new topic:", error);
      });
  };

  const handleTimerExpired = useCallback(
    (topicId) => {
      setIsRefetching(true);
      // Refetch all topics when a timer expires
      getAllTopics(
        pagination.page,
        filters.pageSize,
        filters.platform || null,
        filters.sortBy === "most_counts" ? true : null,
        filters.type || null, // ✅ add
        filters.sortBy === "oldest" ? true : null,
      ).finally(() => {
        setIsRefetching(false);
      });
    },
    [pagination.page, filters],
  );
  const timers = useCountdownTimers(allTopics, handleTimerExpired);

  const getAllTopics = useCallback(
    async (
      page = 1,
      pageSize = null,
      platform = null,
      mostCounts = null,
      type = null,
      oldest = null,
    ) => {
      try {
        const params = {
          page,
          page_size: pageSize || filters.pageSize,
        };

        // Only add filter params if they have values
        if (platform) params.platform = platform;
        if (mostCounts !== null) params.most_counts = mostCounts;
        if (type !== null) params.type = type;
        if (oldest !== null) params.oldest = oldest;

        const response = await modelsApi.get(`/topics`, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const {
          data,
          page: currentPage,
          page_size,
          total_items,
          total_pages,
        } = response.data;

        setAllTopics(data);
        setPagination({
          page: currentPage,
          page_size,
          total_items,
          total_pages,
        });
      } catch (err) {
        console.error("Error fetching all topics:", err);
      }
    },
    [token, filters.pageSize],
  );

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));

    // Reset to page 1 when filters change
    setPagination((prev) => ({
      ...prev,
      page: 1,
    }));
  };

  // Apply filters whenever they change
  useEffect(() => {
    const { platform, sortBy, pageSize, type } = filters;

    let mostCounts = null;
    let oldest = null;

    if (sortBy === "most_counts") {
      mostCounts = true;
    } else if (sortBy === "oldest") {
      oldest = true;
    }

    getAllTopics(
      1, // page
      pageSize, // size
      platform || null, // platform
      mostCounts, // most
      type || null, // ✅ type
      oldest, // oldest
    );
  }, [filters, getAllTopics]);

  const handleNextTopic = () => {
    setSelectedTopicIndex((prev) => {
      if (prev === null) return prev;
      if (prev < allTopics.length - 1) return prev + 1;
      return prev;
    });
  };

  const handlePrevTopic = () => {
    setSelectedTopicIndex((prev) => {
      if (prev === null) return prev;
      if (prev > 0) return prev - 1;
      return prev;
    });
  };
  const handleDrawerClose = () => {
    setViewDrawerOpen(false);
    setSelectedTopicIndex(null);
  };

  const getGlobalPostCount = useCallback(async () => {
    try {
      const { data } = await modelsApi.get(`/topics/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        totalCount: data?.total_count ?? 0,
        thisWeekCount: data?.this_week_count ?? 0,
        from: data?.from ?? null,
        to: data?.to ?? null,
      };
    } catch (err) {
      console.error("Failed to fetch global post count", err);
      return {
        totalCount: 0,
        thisWeekCount: 0,
        from: null,
        to: null,
      };
    }
  }, [token]);

  const getUserPostCount = useCallback(async () => {
    try {
      const { data } = await modelsApi.get(`/topics/user/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return {
        totalCount: data?.total_count ?? 0,
        thisWeekCount: data?.this_week_count ?? 0,
        email: data?.email ?? "",
        username: data?.username ?? "",
        from: data?.from ?? null,
        to: data?.to ?? null,
      };
    } catch (err) {
      console.error("Failed to fetch user post count", err);
      return {
        totalCount: 0,
        thisWeekCount: 0,
        email: "",
        username: "",
        from: null,
        to: null,
      };
    }
  }, [token]);

  useEffect(() => {
    const loadCounts = async () => {
      const [global, user] = await Promise.all([
        getGlobalPostCount(),
        getUserPostCount(),
      ]);

      setGlobalCount(global);
      setUserCount(user);
    };

    loadCounts();
  }, [getGlobalPostCount, getUserPostCount]);

  // Open SSE once on mount (REST fetch already happens via the filters effect above).
  // Close on unmount to clean up the persistent connection.
  useEffect(() => {
    openSSE();
    return () => closeSSE();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!location?.state?.autoRun) return;

    // Basic validation: require topic non-empty, type selected, and at least one platform
    const hasTopic = formData?.topic && formData.topic.trim().length > 0;
    const hasType = formData?.type && formData.type.trim().length > 0;
    const hasPlatforms =
      Array.isArray(formData?.platforms) && formData.platforms.length > 0;

    if (!hasTopic || !hasType || !hasPlatforms) {
      // If any required field is missing, do not auto-create.
      // Clear the autoRun flag from location so reloads don't repeatedly attempt.
      // Use replace to avoid creating a new history entry.
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const t = setTimeout(() => {
      try {
        handleNewTopic();
      } catch (err) {
        console.error("Auto-create topic failed:", err);
      } finally {
        // Clear the autoRun flag so page refresh won't re-trigger the create
        navigate(location.pathname, { replace: true, state: {} });
      }
    }, 200); // slight delay to allow context updates to settle

    return () => clearTimeout(t);
    // We want to run whenever location or formData change (so we can detect when formData has been populated)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.autoRun, formData]);

  return (
    <div className="container">
      <NavBar />
      <CustomDrawer
        open={viewdrawerOpen}
        onClose={handleDrawerClose}
        topicData={selectedTopic}
        selectedIndex={selectedTopicIndex}
        totalCount={allTopics.length}
        onNext={handleNextTopic}
        onPrev={handlePrevTopic}
      />

      <main>
        <div
          className="topic-container"
          style={{
            minHeight: "100vh",
            width: "100%",
            padding: "25px",
            backgroundColor: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Typography
                variant="h4"
                className="drawer-title"
                sx={{
                  fontWeight: 600,
                  fontFamily: "Host Grotesk, sans-serif",
                }}
              >
                Topics Manager
              </Typography>
              {/* SSE live indicator */}
              <div
                title={
                  sseStatus === "live"
                    ? "Live updates active"
                    : sseStatus === "connecting"
                      ? "Connecting…"
                      : sseStatus === "error"
                        ? "Disconnected"
                        : ""
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  padding: "3px 10px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  background:
                    sseStatus === "live"
                      ? "rgba(16,185,129,0.08)"
                      : sseStatus === "connecting"
                        ? "rgba(245,158,11,0.08)"
                        : sseStatus === "error"
                          ? "rgba(239,68,68,0.08)"
                          : "transparent",
                  border:
                    sseStatus === "live"
                      ? "1px solid rgba(16,185,129,0.3)"
                      : sseStatus === "connecting"
                        ? "1px solid rgba(245,158,11,0.3)"
                        : sseStatus === "error"
                          ? "1px solid rgba(239,68,68,0.3)"
                          : "none",
                  color:
                    sseStatus === "live"
                      ? "#10b981"
                      : sseStatus === "connecting"
                        ? "#d97706"
                        : sseStatus === "error"
                          ? "#dc2626"
                          : "transparent",
                  transition: "all 0.3s ease",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor:
                      sseStatus === "live"
                        ? "#10b981"
                        : sseStatus === "connecting"
                          ? "#f59e0b"
                          : "#ef4444",
                    flexShrink: 0,
                    animation:
                      sseStatus === "live" || sseStatus === "connecting"
                        ? "ssePulse 1.5s ease-in-out infinite"
                        : "none",
                  }}
                />
                {sseStatus === "live"
                  ? "Live"
                  : sseStatus === "connecting"
                    ? "Connecting…"
                    : sseStatus === "error"
                      ? "Disconnected"
                      : null}
              </div>
              <style>{`@keyframes ssePulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            </div>
            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {/* Create New Topic Button */}
              <Button
                variant="contained"
                onClick={() => setShowForm((prev) => !prev)}
                sx={{
                  height: "44px",
                  padding: "0 28px",
                  background:
                    "linear-gradient(180deg, #4079f0 0%, #2a5fe6 100%)",
                  border: "none",
                  borderRadius: "22px",
                  color: "white",
                  fontWeight: 600,
                  fontSize: "15px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 12px rgba(53, 114, 239, 0.35)",
                  fontFamily: "Host Grotesk, sans-serif",
                  textTransform: "none",
                  alignSelf: "center",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #2a5fe6 0%, #1d4ed8 100%)",
                    boxShadow: "0 6px 16px rgba(53, 114, 239, 0.45)",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                {showForm ? "Cancel" : "Create New Topic"}
              </Button>
            </div>
          </div>
          {/* Animated Div */}
          {/* AnimatePresence Wrapper */}

          <AnimatePresence initial={false}>
            {showForm && (
              <motion.div
                key="expandable"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35, ease: [0.25, 0.8, 0.25, 1] }}
                style={{ overflow: "hidden", marginBottom: "16px" }}
              >
                {/* Compact form card — matches CrawlForm style */}
                <div
                  style={{
                    background: "#ffffff",
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    boxShadow:
                      "0 4px 6px -1px rgba(15,23,42,0.07), 0 2px 4px -2px rgba(15,23,42,0.05)",
                    overflow: "hidden",
                  }}
                >
                  {/* Header strip */}
                  <div
                    style={{
                      padding: "12px 20px",
                      borderBottom: "1px solid #e2e8f0",
                      background:
                        "linear-gradient(135deg, #f0eeff 0%, #ffffff 100%)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "13px",
                        color: "#374151",
                        fontFamily: "Host Grotesk, sans-serif",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      New Topic
                    </Typography>
                  </div>

                  {/* Fields row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "16px 20px",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Topic input */}
                    <TextField
                      label="Enter Topic"
                      value={formData.topic}
                      onChange={(e) =>
                        handleInputChange("topic", e.target.value)
                      }
                      sx={{
                        flex: 1,
                        minWidth: 200,
                        "& .MuiOutlinedInput-root": {
                          height: "44px",
                          borderRadius: "10px",
                          fontFamily: "Host Grotesk, sans-serif",
                          fontSize: "14px",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#a5b4fc",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6c63ff",
                            borderWidth: "1.5px",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontFamily: "Host Grotesk, sans-serif",
                          fontSize: "14px",
                          "&.Mui-focused": { color: "#6c63ff" },
                        },
                      }}
                    />

                    {/* Platform select */}
                    <div style={{ minWidth: 200 }}>
                      <PlatformsSelect
                        value={formData.platforms}
                        onChange={(selected) =>
                          setFormData((prev) => ({
                            ...prev,
                            platforms: selected,
                          }))
                        }
                      />
                    </div>

                    {/* Objective pill toggle */}
                    <div
                      style={{
                        display: "inline-flex",
                        backgroundColor: "#f1f5f9",
                        borderRadius: "12px",
                        padding: "4px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
                        flexShrink: 0,
                      }}
                    >
                      {["section", "sentiment"].map((opt) => {
                        const active = formData.type === opt;
                        return (
                          <button
                            key={opt}
                            onClick={() =>
                              setFormData({ ...formData, type: opt })
                            }
                            style={{
                              border: "none",
                              outline: "none",
                              cursor: "pointer",
                              fontFamily: "Host Grotesk, sans-serif",
                              fontWeight: active ? 600 : 450,
                              fontSize: "13px",
                              borderRadius: "9px",
                              padding: "8px 18px",
                              color: active ? "#fff" : "#64748b",
                              background: active
                                ? "linear-gradient(180deg, #4079f0 0%, #2a5fe6 100%)"
                                : "transparent",
                              boxShadow: active
                                ? "0 2px 8px rgba(53,114,239,0.3)"
                                : "none",
                              transition:
                                "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                          >
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </button>
                        );
                      })}
                    </div>

                    {/* Confirm button */}
                    {(() => {
                      const isValid =
                        !!(formData?.topic?.trim?.() || "") &&
                        (formData?.platforms?.length ?? 0) > 0 &&
                        !!(formData?.type?.trim?.() || "");
                      return (
                        <Button
                          variant="contained"
                          onClick={handleConfirm}
                          disabled={!isValid}
                          sx={{
                            height: "44px",
                            minWidth: "120px",
                            padding: "0 20px",
                            borderRadius: "10px",
                            fontFamily: "Host Grotesk, sans-serif",
                            fontSize: "13px",
                            fontWeight: 600,
                            letterSpacing: "0.3px",
                            textTransform: "none",
                            flexShrink: 0,
                            background: isValid
                              ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                              : "transparent",
                            border: isValid
                              ? "none"
                              : "1.5px solid #e2e8f0",
                            color: isValid ? "#fff" : "#9ca3af",
                            boxShadow: isValid
                              ? "0 4px 14px rgba(16,185,129,0.25)"
                              : "none",
                            transition:
                              "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": isValid
                              ? {
                                  background:
                                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                  boxShadow:
                                    "0 6px 20px rgba(16,185,129,0.35)",
                                  transform: "translateY(-1px)",
                                }
                              : {},
                            "&.Mui-disabled": {
                              background: "transparent",
                              border: "1.5px solid #e2e8f0",
                              color: "#9ca3af",
                              boxShadow: "none",
                            },
                          }}
                        >
                          Create Topic
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compact Analytics + Pagination */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "15px",
              marginBottom: "15px",
              gap: "20px",
            }}
          >
            {/* LEFT: Compact analytics (hide when form open) */}
            {!showForm && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div className="mini-analytics">
                  <span className="mini-label">All-Time Posts</span>
                  <span className="mini-value">
                    {globalCount.totalCount.toLocaleString()}
                  </span>
                </div>
                <div className="mini-analytics highlight-amber">
                  <span className="mini-label">Weekly Posts</span>
                  <span className="mini-value">
                    {globalCount.thisWeekCount.toLocaleString()}
                  </span>
                </div>
                <div
                  style={{
                    width: "1px",
                    height: "32px",
                    background: "#e5e7eb",
                    flexShrink: 0,
                  }}
                />
                <div className="mini-analytics highlight-green">
                  <span className="mini-label">My Total Posts</span>
                  <span className="mini-value">
                    {userCount.totalCount.toLocaleString()}
                  </span>
                </div>
                <div className="mini-analytics highlight-purple">
                  <span className="mini-label">My Weekly Posts</span>
                  <span className="mini-value">
                    {userCount.thisWeekCount.toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* RIGHT: filters always left of the count pill, grouped together */}
            {allTopics?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginLeft: "auto",
                }}
              >
                <TopicFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontFamily: "Host Grotesk, sans-serif",
                    fontSize: "13px",
                    color: "#6b7280",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    padding: "10px 16px",
                    background: "#f9fafb",
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  Showing {allTopics.length} of {pagination.total_items} topics
                  {" · "} Page {pagination.page} of {pagination.total_pages}
                </Typography>
              </div>
            )}
          </div>

          {allTopics && allTopics.length === 0 && !isRefetching ? (
            <div
              className="three-body-problem"
              style={{
                height: "80vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div className="three-body">
                <div className="three-body__dot"></div>
                <div className="three-body__dot"></div>
                <div className="three-body__dot"></div>
              </div>
            </div>
          ) : (
            <TopicCards
              allTopics={allTopics}
              timers={timers}
              setSelectedTopicIndex={setSelectedTopicIndex}
              setViewDrawerOpen={setViewDrawerOpen}
              handleEdit={handleEdit}
              getAllTopics={getAllTopics}
              filters={filters}
              pagination={pagination}
              sseStatus={sseStatus}
            />
          )}

          <EditModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            sessionData={sessionData}
            onSave={handleSave}
          />
        </div>
      </main>
      <ReactProSidebar />
    </div>
  );
};

export default TopicQuery;
